import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { getInvoicePrefs, matchesSubscriber, type InvoicePrefSubscriber } from "../../api/invoicePrefs";
import { SubscriberRechargeDialog } from "./SubscriberRechargeDialog";
import { SubscriberReceiptsDialog } from "./SubscriberReceiptsDialog";
import { SubscriberDisableDialog } from "./SubscriberDisableDialog";
import { SubscriberInstallationsDialog } from "./SubscriberInstallationsDialog";
import { SubscriberDiagnoseDialog } from "./SubscriberDiagnoseDialog";
import { SubscriberOnuDialog } from "./SubscriberOnuDialog";
import { SubscriberRadiusTestDialog } from "../tools/SubscriberRadiusTestDialog";
import { isDisabled } from "../../api/subscriberDisable";
import { dueOrder, expiryMs, timeLeftLabel } from "../../api/subscriberRecharge";
import { canDisableSubscriber, canManageInstallations, canOpenSubscriberScreen, canReadReceipts } from "../../components/nav";

/** At most this many rows before a search narrows them. */
const SHOWN = 50;

/**
 * Billing › Recharge (owner 2026-10-10): replaces the PHP recharge page. Find
 * the subscriber by mobile, name or account, then Recharge or open Receipts.
 * Disable (owner 2026-10-14) stops a subscriber signing in; a disabled one is
 * marked and offered neither Recharge nor Disable.
 */
const TITLES = {
  recharge: {
    title: "Recharge",
    text: "Give a subscriber a plan and record the cash or UPI payment you took. Every device on the account gets the plan; one receipt is recorded.",
  },
  disable: {
    title: "Disable user",
    text: "Stop a subscriber signing in. Nothing is deleted: their record, receipts and history are kept, as the law requires.",
  },
  voucher: {
    title: "Print voucher",
    text: "Print a voucher slip for a recharge: pick the subscriber, then the receipt. Recharge first if they have not paid yet.",
  },
  diagnose: {
    title: "User diagnose",
    text: "Find out why a subscriber cannot connect: sign-in, password, plan, expiry and sessions, checked for every device.",
  },
  radiustest: {
    title: "Test RADIUS login",
    text: "Check a subscriber's username and password against RADIUS, the way their router would, and see why it is refused. The password is not kept.",
  },
  onu: {
    title: "Change Device MAC",
    text: "The subscriber's ONU or router was replaced: move sign-in to the new device's MAC address. The plan, receipts and history move with it.",
  },
} as const;

export type RechargeScreenMode = keyof typeof TITLES;

/**
 * mode (owner 2026-10-15, the PHP links removed): "recharge" is Billing ›
 * Recharge; "disable" is Users › Disable user (was PHP delete_user.php);
 * "voucher" is Billing › Print voucher (was PHP print_voucher.php);
 * "diagnose" is Users › User diagnose (was PHP userdiagnose.php) and "onu" is
 * Devices › Change ONU MAC (was PHP onuchange.php), owner 2026-10-17.
 */
export function RechargeScreen({ mode = "recharge" }: { mode?: RechargeScreenMode }) {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const allowed = canOpenSubscriberScreen(mode, identity.roles);
  const mayInstall = canManageInstallations(identity.roles);
  const mayReceipts = canReadReceipts(identity.roles);
  const list = useQuery({
    queryKey: ["invoiceprefs", ns],
    enabled: Boolean(identity.token && ns && allowed),
    queryFn: ({ signal }) => getInvoicePrefs(identity, ns, signal),
    retry: false,
  });
  const [q, setQ] = useState("");
  const [rechargeFor, setRechargeFor] = useState<InvoicePrefSubscriber | null>(null);
  const [receiptsFor, setReceiptsFor] = useState<InvoicePrefSubscriber | null>(null);
  const [disableFor, setDisableFor] = useState<InvoicePrefSubscriber | null>(null);
  const [installationsFor, setInstallationsFor] = useState<InvoicePrefSubscriber | null>(null);
  const [diagnoseFor, setDiagnoseFor] = useState<InvoicePrefSubscriber | null>(null);
  const [onuFor, setOnuFor] = useState<InvoicePrefSubscriber | null>(null);
  const [radiusFor, setRadiusFor] = useState<InvoicePrefSubscriber | null>(null);
  const mayDisable = canDisableSubscriber(identity.roles);
  const now = Date.now();
  const matched = (list.data?.subscribers ?? []).filter((s) => matchesSubscriber(s, q));
  // Owner 2026-10-28: on Recharge the subscriber due soonest comes first.
  const rows = mode === "recharge" ? dueOrder(matched, now) : matched;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography variant="h5" component="h1">
          {TITLES[mode].title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {TITLES[mode].text}
        </Typography>
      </Box>
      {!allowed && (
        <Alert severity="warning">
          This login cannot use {TITLES[mode].title}: it holds {identity.roles.length ? identity.roles.join(", ") : "no role"}. Ask the operator to give
          you the role this screen needs.
        </Alert>
      )}
      {list.error && <Alert severity="error">{list.error instanceof Error ? list.error.message : String(list.error)}</Alert>}
      {list.data && <TextField size="small" label="Search mobile, name or account" autoFocus inputProps={{ inputMode: "search" }} value={q} onChange={(e) => setQ(e.target.value)} sx={{ maxWidth: 360 }} />}
      {list.data && rows.length === 0 && <Alert severity="info">{q ? "No subscriber matches." : "No subscribers yet."}</Alert>}
      {rows.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Subscriber</TableCell>
                {mode === "recharge" && <TableCell>Plan</TableCell>}
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.slice(0, SHOWN).map((s) => (
                <TableRow key={s.accountid}>
                  <TableCell>
                    {s.name || s.username || <code>{s.accountid}</code>}
                    {isDisabled(s) && <Chip size="small" label="Disabled" sx={{ ml: 1 }} />}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {[s.mobile, s.username].filter(Boolean).join(" · ")}
                    </Typography>
                  </TableCell>
                  {mode === "recharge" && (
                    <TableCell>
                      {s.plan || <Typography variant="caption" color="text.secondary">No plan</Typography>}
                      {s.expiry && (
                        <Typography variant="caption" display="block" color={(expiryMs(s.expiry) ?? 0) - now < 86_400_000 ? "error" : "text.secondary"}>
                          {timeLeftLabel(s.expiry, now)} · {s.expiry} IST
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  <TableCell align="right">
                    {mode === "diagnose" && (
                      <Button size="small" variant="contained" onClick={() => setDiagnoseFor(s)}>
                        Diagnose
                      </Button>
                    )}
                    {mode === "radiustest" && (
                      <Button size="small" variant="contained" onClick={() => setRadiusFor(s)}>
                        Test login
                      </Button>
                    )}
                    {mode === "onu" &&
                      (isDisabled(s) ? (
                        <Typography variant="caption" color="text.secondary">Disabled</Typography>
                      ) : (
                        <Button size="small" variant="contained" onClick={() => setOnuFor(s)}>
                          Change MAC
                        </Button>
                      ))}
                    {mode === "voucher" && (
                      <Button size="small" variant="contained" onClick={() => setReceiptsFor(s)}>
                        Choose receipt
                      </Button>
                    )}
                    {mode === "disable" &&
                      (isDisabled(s) ? (
                        <Typography variant="caption" color="text.secondary">Already disabled</Typography>
                      ) : mayDisable ? (
                        <Button size="small" variant="contained" color="error" onClick={() => setDisableFor(s)}>
                          Disable
                        </Button>
                      ) : null)}
                    {mode === "recharge" && (
                      <>
                        {mayReceipts && (
                          <Button size="small" onClick={() => setReceiptsFor(s)}>
                            Receipts
                          </Button>
                        )}
                        {mayInstall && (
                          <Button size="small" onClick={() => setInstallationsFor(s)}>
                            Installations
                          </Button>
                        )}
                        {!isDisabled(s) && mayDisable && (
                          <Button size="small" color="error" onClick={() => setDisableFor(s)}>
                            Disable
                          </Button>
                        )}
                        {!isDisabled(s) && (
                          <Button size="small" variant="contained" onClick={() => setRechargeFor(s)}>
                            Recharge
                          </Button>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {rows.length > SHOWN && (
        <Typography variant="caption" color="text.secondary">
          Showing {SHOWN} of {rows.length}; search to narrow.
        </Typography>
      )}
      <SubscriberRechargeDialog ns={ns} subscriber={rechargeFor} onClose={() => setRechargeFor(null)} />
      <SubscriberReceiptsDialog ns={ns} subscriber={receiptsFor} onClose={() => setReceiptsFor(null)} />
      <SubscriberDisableDialog ns={ns} subscriber={disableFor} onClose={() => setDisableFor(null)} />
      <SubscriberInstallationsDialog ns={ns} subscriber={installationsFor} onClose={() => setInstallationsFor(null)} />
      <SubscriberDiagnoseDialog ns={ns} subscriber={diagnoseFor} onClose={() => setDiagnoseFor(null)} />
      <SubscriberOnuDialog ns={ns} subscriber={onuFor} onClose={() => setOnuFor(null)} />
      <SubscriberRadiusTestDialog ns={ns} subscriber={radiusFor} onClose={() => setRadiusFor(null)} />
    </Box>
  );
}
